import { z } from "zod";
import { apiService, organizationIdHeader } from "../api.service";
import {
	type AuditLogDisplay,
	AuditLogDisplaySchema,
	type FeatureCollection,
	FeatureCollectionSchema,
} from "../job/job.types";
import {
	appendAttachmentRequirementTagId,
	createTypeAttachmentRequirementTag,
	deleteTypeAttachmentRequirementTag,
	editTypeAttachmentRequirementTag,
	getTypeAttachmentRequirement,
	putTypeAttachmentRequirement,
} from "../shared/attachment-requirement.api";

const AuditLogArray = z.array(AuditLogDisplaySchema);

import { shouldShowOrgAdminAssetTypesTableRow } from "@/utils/asset-type-detail/asset-type-detail.utils";
import { isRequestAborted } from "@/utils/is-request-aborted";
import {
	type GeometrySourceDisplay,
	GeometrySourcesArraySchema,
	type GpsMethodDisplay,
	GpsMethodsArraySchema,
} from "../shared/geometry-catalog.types";
import type { CreateNoteInfo, NoteDisplay } from "../shared/note.types";
import { CreateNoteInfoSchema } from "../shared/note.types";
import type {
	AssetDisplay,
	AssetDisplayWithUsers,
	AssetFileDisplay,
	AssetFileParams,
	AssetGeometryIcons,
	AssetInstanceListParams,
	AssetNoteParams,
	AssetParams,
	AssetType,
	AssetTypeDisplay,
	AssetTypesAllParams,
	AssetTypesListParams,
	CreateAssetInfo,
	CreateAssetTypeInfo,
	EditAssetFileInfo,
	EditAssetInfo,
	EditAssetTypeInfo,
	GetAssetStatusesResponse,
	ServiceResult,
} from "./asset.types";
import {
	AssetDisplayWithUsersSchema,
	AssetFileDisplayArraySchema,
	AssetGeometryTypesArraySchema,
	AssetNoteDisplayArraySchema,
	AssetStatusesArraySchema,
	AssetsArraySchema,
	AssetTypeDisplaySchema,
	AssetTypesArraySchema,
	CreateAssetInfoSchema,
	CreateAssetTypeInfoSchema,
	EditAssetInfoSchema,
	EditAssetTypeInfoSchema,
	ServiceResultSchema,
} from "./asset.types";

/**
 * Flatten AssetParams so nested BoundingBox uses dot notation expected by the API.
 */
function toAssetQueryParams(
	params?: AssetParams,
):
	| Record<string, string | number | boolean | string[] | undefined>
	| undefined {
	if (!params) return undefined;
	const flat: Record<string, string | number | boolean | string[] | undefined> =
		{};
	const { BoundingBox, ...rest } = params;
	if (BoundingBox) {
		if (BoundingBox.MinLatitude !== undefined)
			flat["BoundingBox.MinLatitude"] = BoundingBox.MinLatitude;
		if (BoundingBox.MaxLatitude !== undefined)
			flat["BoundingBox.MaxLatitude"] = BoundingBox.MaxLatitude;
		if (BoundingBox.MinLongitude !== undefined)
			flat["BoundingBox.MinLongitude"] = BoundingBox.MinLongitude;
		if (BoundingBox.MaxLongitude !== undefined)
			flat["BoundingBox.MaxLongitude"] = BoundingBox.MaxLongitude;
	}
	for (const [k, v] of Object.entries(rest)) {
		if (v !== undefined) flat[k] = v as string | number | boolean | string[];
	}
	return Object.keys(flat).length ? flat : undefined;
}

export class AssetApiError extends Error {
	constructor(
		message: string,
		public readonly cause?: unknown,
		public readonly statusCode?: number,
	) {
		super(message);
		this.name = "AssetApiError";
	}

	static fromServiceResult(result: ServiceResult): AssetApiError {
		return new AssetApiError(result.message ?? "Asset operation failed");
	}
}

/** Longer than default API timeout; for troubleshooting slow asset-type list/search calls. May remove later. */
const ASSET_TYPES_LIST_TIMEOUT_MS = 20_000;

/** Form copy on a real asset type can outlast the shared API timeout. */
const DUPLICATE_ASSET_TYPE_TIMEOUT_MS = 60_000;

const ASSET_TYPE_ORDER_BY_ACCESSOR: Record<
	string,
	(row: AssetType) => string | number | null | undefined
> = {
	Name: (row) => row.name ?? "",
	Description: (row) => row.description ?? "",
	Id: (row) => row.id ?? "",
};

function isOrgScopedAssetTypesQuery(
	input?: AssetTypesListParams,
): input is AssetTypesListParams {
	return !!(input?.OrganizationId || input?.SubOrganizationId);
}

function stripEnabledParam(
	input?: AssetTypesListParams,
): AssetTypesListParams | undefined {
	if (!input) return undefined;
	const { Enabled: _enabled, ...rest } = input;
	return rest;
}

function resolveMergedAssetTypeEnabled(
	row: AssetType,
	fromDisabledFetch: boolean,
): boolean {
	if (fromDisabledFetch) return false;
	if (row.enabled === false) return false;
	return row.enabled ?? true;
}

function mergeAssetTypesById(
	enabledRows: AssetType[],
	disabledRows: AssetType[],
): AssetType[] {
	const byId = new Map<string, AssetType>();
	for (const row of enabledRows) {
		if (row.id) {
			byId.set(row.id, {
				...row,
				enabled: resolveMergedAssetTypeEnabled(row, false),
			});
		}
	}
	for (const row of disabledRows) {
		if (row.id) {
			byId.set(row.id, {
				...row,
				enabled: resolveMergedAssetTypeEnabled(row, true),
			});
		}
	}
	return [...byId.values()];
}

function filterOrgAdminConfigurationTableRows(rows: AssetType[]): AssetType[] {
	return rows.filter(shouldShowOrgAdminAssetTypesTableRow);
}

function sortAssetTypes(
	rows: AssetType[],
	input?: AssetTypesListParams,
): AssetType[] {
	const fields = input?.["OrderBy.Fields"] ?? ["Name"];
	const descending = input?.["OrderBy.Descending"] ?? false;
	const field = fields[0] ?? "Name";
	const accessor =
		ASSET_TYPE_ORDER_BY_ACCESSOR[field] ?? ASSET_TYPE_ORDER_BY_ACCESSOR.Name;

	return [...rows].sort((a, b) => {
		const av = accessor(a);
		const bv = accessor(b);
		const cmp = String(av).localeCompare(String(bv), undefined, {
			sensitivity: "base",
			numeric: true,
		});
		return descending ? -cmp : cmp;
	});
}

export class AssetService {
	/**
	 * Get all asset types (simple list)
	 * GET /assets/types
	 */
	async getAssetTypes(input?: AssetTypesAllParams): Promise<AssetType[]> {
		try {
			// When scope filters are present, `/assets/types` ignores `OrganizationId`.
			// Use paginated endpoints (which do honor org filters) to build the full list.
			if (input?.OrganizationId || input?.subOrganizationId) {
				const scopedParams: AssetTypesListParams = {
					...(input.OrganizationId
						? { OrganizationId: input.OrganizationId }
						: {}),
					...(input.subOrganizationId
						? { SubOrganizationId: input.subOrganizationId }
						: {}),
				};
				const total = await this.getAssetTypesCount(scopedParams);
				if (total <= 0) {
					return [];
				}
				const fetchCount = Math.min(total, 10_000);
				const scopedRows = await this.getAssetTypesPage(
					1,
					fetchCount,
					scopedParams,
				);
				return scopedRows;
			}

			const params: Record<string, string> = {};
			if (input?.subOrganizationId) {
				params.subOrganizationId = input.subOrganizationId;
			}
			if (input?.OrganizationId) {
				params.OrganizationId = input.OrganizationId;
			}
			const response = await apiService.get<unknown>("/assets/types", {
				params: Object.keys(params).length > 0 ? params : undefined,
				timeout: ASSET_TYPES_LIST_TIMEOUT_MS,
			});
			return AssetTypesArraySchema.parse(response);
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to get asset types: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get asset types count
	 * GET /assets/types/count
	 */
	async getAssetTypesCount(input?: AssetTypesListParams): Promise<number> {
		try {
			if (isOrgScopedAssetTypesQuery(input) && input.Enabled === undefined) {
				const merged = await this.getOrgScopedAssetTypesMerged(input);
				return merged.length;
			}

			return this.fetchAssetTypesCountSingle(input);
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to get asset types count: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	private async fetchAssetTypesCountSingle(
		input?: AssetTypesListParams,
	): Promise<number> {
		const response = await apiService.get<number>("/assets/types/count", {
			params: input,
			timeout: ASSET_TYPES_LIST_TIMEOUT_MS,
		});
		return z.number().parse(response);
	}

	/**
	 * Get a single asset by ID
	 * GET /assets/{id}
	 */
	async getAsset(
		id: string,
		options?: { organizationId?: string },
	): Promise<AssetDisplayWithUsers> {
		try {
			const response = await apiService.get<AssetDisplayWithUsers>(
				`/assets/${id}`,
				{
					headers: organizationIdHeader(options?.organizationId),
				},
			);
			return AssetDisplayWithUsersSchema.parse(response);
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to get asset ${id}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get valid statuses for an asset
	 * GET /assets/{id}/valid-statuses
	 *
	 * @returns Array of valid asset statuses for the specified asset
	 */
	async getAssetValidStatuses(id: string): Promise<GetAssetStatusesResponse> {
		try {
			const response = await apiService.get<GetAssetStatusesResponse>(
				`/assets/${id}/valid-statuses`,
			);

			return AssetStatusesArraySchema.parse(response);
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to get valid statuses for asset ${id}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * GET /assets/{start}/{count}
	 */
	async listAssets(
		start: number,
		count: number,
		params?: AssetInstanceListParams,
	): Promise<AssetDisplay[]> {
		try {
			const response = await apiService.get<unknown>(
				`/assets/${start}/${count}`,
				{
					params,
					headers: organizationIdHeader(params?.OrganizationId),
				},
			);
			return AssetsArraySchema.parse(response);
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to list assets: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * PUT /assets/{assetId}
	 */
	async updateAsset(
		assetId: string,
		body: EditAssetInfo,
	): Promise<ServiceResult> {
		try {
			const payload = EditAssetInfoSchema.parse(body);
			const response = await apiService.put<ServiceResult>(
				`/assets/${assetId}`,
				payload,
				{
					headers: organizationIdHeader(payload.organizationId ?? undefined),
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw AssetApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to update asset: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Create an asset instance.
	 * POST /assets
	 */
	async createAsset(data: CreateAssetInfo): Promise<ServiceResult> {
		try {
			const payload = CreateAssetInfoSchema.parse(data);
			const response = await apiService.post<ServiceResult>(
				"/assets",
				payload,
				{
					headers: organizationIdHeader(payload.organizationId ?? undefined),
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw AssetApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to create asset: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * DELETE /assets/{assetId}
	 */
	async deleteAsset(
		assetId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const response = await apiService.delete<unknown>(`/assets/${assetId}`, {
				headers: organizationIdHeader(options?.organizationId),
			});
			if (response == null || response === "") {
				return { success: true, message: null, affectedId: null };
			}
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw AssetApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to delete asset: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * GET /assets/{id}/history/{start}/{count}
	 */
	async getAssetHistory(
		assetId: string,
		start: number,
		count: number,
	): Promise<AuditLogDisplay[]> {
		try {
			const response = await apiService.get<AuditLogDisplay[]>(
				`/assets/${assetId}/history/${start}/${count}`,
			);
			return AuditLogArray.parse(response);
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to get asset history: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * GET /assets/{id}/history/count
	 */
	async getAssetHistoryCount(assetId: string): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/assets/${assetId}/history/count`,
			);
			return z.number().parse(response);
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to get asset history count: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get assets count (supports AssetTypeIds filter)
	 * GET /assets/count
	 */
	async getAssetsCount(
		input?: AssetParams,
		options?: { signal?: AbortSignal },
	): Promise<number> {
		try {
			const response = await apiService.get<number>("/assets/count", {
				params: toAssetQueryParams(input),
				headers: organizationIdHeader(input?.OrganizationId),
				signal: options?.signal,
			});
			return z.number().parse(response);
		} catch (error) {
			if (isRequestAborted(error)) throw error;
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to get assets count: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Export asset instances to CSV.
	 * GET /assets/{start}/{count}/export
	 *
	 * @param start - Starting index (1-based)
	 * @param count - Number of assets to export
	 * @param params - Optional query parameters (same as list/count) plus columns
	 * @returns Blob containing CSV data
	 */
	async exportAssets(
		start: number,
		count: number,
		params?: AssetParams & { columns?: string },
	): Promise<Blob> {
		try {
			const baseParams = toAssetQueryParams(params);
			const queryParams = baseParams ? { ...baseParams } : {};
			if (params && "columns" in params && params.columns !== undefined) {
				queryParams.columns = params.columns;
			}
			return await apiService.get<Blob>(`/assets/${start}/${count}/export`, {
				params: queryParams,
				responseType: "blob",
				headers: {
					Accept: "text/csv",
					...organizationIdHeader(params?.OrganizationId),
				} as Record<string, string>,
			});
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to export assets: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get assets as GeoJSON features
	 * GET /assets/features/{start}/{count}
	 */
	async getAssetsFeatures(
		start: number = 1,
		count: number = 10,
		params?: AssetParams,
	): Promise<FeatureCollection> {
		try {
			const response = await apiService.get<FeatureCollection>(
				`/assets/features/${start}/${count}`,
				{
					params: toAssetQueryParams(params),
					headers: organizationIdHeader(params?.OrganizationId),
				},
			);
			return FeatureCollectionSchema.parse(response);
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to get assets features: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get asset type detail
	 * GET /assets/types/{assetTypeId}
	 */
	async getAssetTypeDetail(
		assetTypeId: string,
		options?: { organizationId?: string },
	): Promise<AssetTypeDisplay> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.get<unknown>(
				`/assets/types/${assetTypeId}`,
				{
					headers:
						organizationId !== undefined
							? { "organization-id": organizationId }
							: undefined,
				},
			);
			return AssetTypeDisplaySchema.parse(response);
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to get asset type detail: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get a paginated page of asset types
	 * GET /assets/types/{start}/{count}
	 */
	async getAssetTypesPage(
		start: number,
		count: number,
		input?: AssetTypesListParams,
	): Promise<AssetType[]> {
		try {
			if (isOrgScopedAssetTypesQuery(input) && input.Enabled === undefined) {
				const merged = await this.getOrgScopedAssetTypesMerged(input);
				const sorted = sortAssetTypes(merged, input);
				const startIndex = Math.max(0, start - 1);
				return sorted.slice(startIndex, startIndex + count);
			}

			return this.fetchAssetTypesPageSingle(start, count, input);
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to get asset types page: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/** Enabled + disabled org-scoped rows merged for configuration tables. */
	private async getOrgScopedAssetTypesMerged(
		input?: AssetTypesListParams,
	): Promise<AssetType[]> {
		const baseParams = stripEnabledParam(input) ?? {};
		const enabledParams = { ...baseParams, Enabled: true };
		const disabledParams = { ...baseParams, Enabled: false };
		const [enabledCount, disabledCount] = await Promise.all([
			this.fetchAssetTypesCountSingle(enabledParams),
			this.fetchAssetTypesCountSingle(disabledParams),
		]);

		const [enabledRows, disabledRows] = await Promise.all([
			enabledCount > 0
				? this.fetchAssetTypesPageSingle(
						1,
						Math.min(enabledCount, 10_000),
						enabledParams,
					)
				: Promise.resolve([]),
			disabledCount > 0
				? this.fetchAssetTypesPageSingle(
						1,
						Math.min(disabledCount, 10_000),
						disabledParams,
					)
				: Promise.resolve([]),
		]);

		return filterOrgAdminConfigurationTableRows(
			mergeAssetTypesById(enabledRows, disabledRows),
		);
	}

	private async fetchAssetTypesPageSingle(
		start: number,
		count: number,
		input?: AssetTypesListParams,
	): Promise<AssetType[]> {
		const response = await apiService.get<unknown>(
			`/assets/types/${start}/${count}`,
			{ params: input, timeout: ASSET_TYPES_LIST_TIMEOUT_MS },
		);
		return AssetTypesArraySchema.parse(response);
	}

	/**
	 * Export asset types to CSV
	 * GET /assets/types/{start}/{count}/export
	 */
	async exportAssetTypesCsv(
		start: number,
		count: number,
		input?: AssetTypesListParams,
		columns?: string | null,
	): Promise<Blob> {
		try {
			const params = {
				...(input ?? {}),
				...(columns != null && columns !== "" ? { columns } : {}),
			};
			return await apiService.get<Blob>(
				`/assets/types/${start}/${count}/export`,
				{
					params,
					headers: {
						Accept: "text/csv",
					},
					responseType: "blob",
				},
			);
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to export asset types: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get asset geometry type options
	 * GET /assets/geometry-types
	 */
	async getAssetGeometryTypes(): Promise<AssetGeometryIcons[]> {
		try {
			const response = await apiService.get<unknown>("/assets/geometry-types");
			return AssetGeometryTypesArraySchema.parse(response);
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to get asset geometry types: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get geometry source options for assets.
	 * GET /assets/geometry-sources
	 */
	async getGeometrySources(): Promise<GeometrySourceDisplay[]> {
		try {
			const response = await apiService.get<unknown>(
				"/assets/geometry-sources",
			);
			return GeometrySourcesArraySchema.parse(response);
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to get asset geometry sources: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get GPS method options for assets.
	 * GET /assets/gps-methods
	 */
	async getGpsMethods(): Promise<GpsMethodDisplay[]> {
		try {
			const response = await apiService.get<unknown>("/assets/gps-methods");
			return GpsMethodsArraySchema.parse(response);
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to get asset GPS methods: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Create a new asset type
	 * POST /assets/types
	 */
	async createAssetType(
		data: CreateAssetTypeInfo,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const validatedData = CreateAssetTypeInfoSchema.parse(data);
			const organizationId = options?.organizationId;

			const response = await apiService.post<ServiceResult>(
				"/assets/types",
				validatedData,
				{
					headers:
						organizationId !== undefined
							? { "organization-id": organizationId }
							: undefined,
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw AssetApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			if (error instanceof z.ZodError) {
				const fieldErrors = error.issues.map((err) => ({
					field: err.path.join("."),
					message: err.message,
				}));
				throw new AssetApiError(
					`Validation failed: ${fieldErrors.map((entry) => `${entry.field}: ${entry.message}`).join(", ")}`,
					error,
				);
			}
			throw new AssetApiError(
				`Failed to create asset type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Duplicate an asset type. The API copies core config and appends " copy" to the name.
	 * POST /assets/types/{assetTypeId}/duplicate
	 */
	async duplicateAssetType(
		assetTypeId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.post<ServiceResult>(
				`/assets/types/${assetTypeId}/duplicate`,
				undefined,
				{
					headers:
						organizationId !== undefined
							? { "organization-id": organizationId }
							: undefined,
					timeout: DUPLICATE_ASSET_TYPE_TIMEOUT_MS,
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw AssetApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to duplicate asset type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Delete an asset type
	 * DELETE /assets/types/{assetTypeId}
	 */
	async deleteAssetType(
		assetTypeId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.delete<ServiceResult>(
				`/assets/types/${assetTypeId}`,
				{
					headers:
						organizationId !== undefined
							? { "organization-id": organizationId }
							: undefined,
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw AssetApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to delete asset type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Update an asset type
	 * PUT /assets/types/{assetTypeId}
	 */
	async updateAssetType(
		assetTypeId: string,
		data: EditAssetTypeInfo,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const validatedData = EditAssetTypeInfoSchema.parse(data);
			const organizationId = options?.organizationId;
			const response = await apiService.put<ServiceResult>(
				`/assets/types/${assetTypeId}`,
				validatedData,
				{
					headers:
						organizationId !== undefined
							? { "organization-id": organizationId }
							: undefined,
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw AssetApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			if (error instanceof z.ZodError) {
				const fieldErrors = error.issues.map((err) => ({
					field: err.path.join("."),
					message: err.message,
				}));
				throw new AssetApiError(
					`Validation failed: ${fieldErrors.map((entry) => `${entry.field}: ${entry.message}`).join(", ")}`,
					error,
				);
			}
			throw new AssetApiError(
				`Failed to update asset type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Enable an asset type for an organization
	 * PUT /assets/types/{assetTypeId}/enable
	 */
	async enableAssetType(
		assetTypeId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.put<ServiceResult>(
				`/assets/types/${assetTypeId}/enable`,
				undefined,
				{
					headers:
						organizationId !== undefined
							? { "organization-id": organizationId }
							: undefined,
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw AssetApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to enable asset type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Disable an asset type for an organization
	 * PUT /assets/types/{assetTypeId}/disable
	 */
	async disableAssetType(
		assetTypeId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.put<ServiceResult>(
				`/assets/types/${assetTypeId}/disable`,
				undefined,
				{
					headers:
						organizationId !== undefined
							? { "organization-id": organizationId }
							: undefined,
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw AssetApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to disable asset type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Enable a specific sub-organization's access to an asset type
	 * POST /assets/types/{assetTypeId}/sub-organization/{subOrgId}/enable
	 */
	async enableAssetTypeSubOrganization(
		assetTypeId: string,
		subOrgId: string,
		options?: { organizationId?: string },
	): Promise<void> {
		const organizationId = options?.organizationId;
		await apiService.post(
			`/assets/types/${assetTypeId}/sub-organization/${subOrgId}/enable`,
			undefined,
			{
				headers:
					organizationId !== undefined
						? { "organization-id": organizationId }
						: undefined,
			},
		);
	}

	/**
	 * Disable a specific sub-organization's access to an asset type
	 * POST /assets/types/{assetTypeId}/sub-organization/{subOrgId}/disable
	 */
	async disableAssetTypeSubOrganization(
		assetTypeId: string,
		subOrgId: string,
		options?: { organizationId?: string },
	): Promise<void> {
		const organizationId = options?.organizationId;
		await apiService.post(
			`/assets/types/${assetTypeId}/sub-organization/${subOrgId}/disable`,
			undefined,
			{
				headers:
					organizationId !== undefined
						? { "organization-id": organizationId }
						: undefined,
			},
		);
	}

	// ==================== ASSET TYPE HISTORY ====================

	/**
	 * Get asset type history count
	 * GET /assets/types/{id}/history/count
	 */
	async getAssetTypeHistoryCount(
		assetTypeId: string,
		organizationId?: string,
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/assets/types/${assetTypeId}/history/count`,
				{
					headers: organizationId
						? { "organization-id": organizationId }
						: undefined,
				},
			);
			return z.number().parse(response);
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to get asset type history count: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get asset type history with pagination
	 * GET /assets/types/{id}/history/{start}/{count}
	 */
	async getAssetTypeHistory(
		assetTypeId: string,
		start: number = 1,
		count: number = 10,
		organizationId?: string,
	): Promise<AuditLogDisplay[]> {
		try {
			const response = await apiService.get<AuditLogDisplay[]>(
				`/assets/types/${assetTypeId}/history/${start}/${count}`,
				{
					headers: organizationId
						? { "organization-id": organizationId }
						: undefined,
				},
			);
			return AuditLogArray.parse(response);
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to get asset type history: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== ASSET FILES ====================

	/**
	 * Flattens {@link AssetFileParams} for ASP.NET query-string binding.
	 */
	private toAssetFileQueryParams(
		params?: AssetFileParams,
	):
		| Record<string, string | number | boolean | string[] | undefined>
		| undefined {
		if (!params) return undefined;
		const flat: Record<
			string,
			string | number | boolean | string[] | undefined
		> = {};
		const { OrderBy, ...rest } = params;
		for (const [k, v] of Object.entries(rest)) {
			if (v !== undefined) flat[k] = v as string | number | boolean | string[];
		}
		if (OrderBy?.Fields !== undefined) flat["OrderBy.Fields"] = OrderBy.Fields;
		if (OrderBy?.Descending !== undefined)
			flat["OrderBy.Descending"] = OrderBy.Descending;
		return Object.keys(flat).length ? flat : undefined;
	}

	/**
	 * GET /assets/{assetId}/files/{start}/{count}
	 */
	async getAssetFiles(
		assetId: string,
		start: number = 1,
		count: number = 50,
		params?: AssetFileParams,
		options?: { organizationId?: string },
	): Promise<AssetFileDisplay[]> {
		try {
			const response = await apiService.get<unknown>(
				`/assets/${assetId}/files/${start}/${count}`,
				{
					params: this.toAssetFileQueryParams(params),
					headers: organizationIdHeader(
						options?.organizationId ?? params?.OrganizationId,
					),
				},
			);
			return AssetFileDisplayArraySchema.parse(response);
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to get files for asset ${assetId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * GET /assets/{assetId}/files/count
	 */
	async getAssetFilesCount(
		assetId: string,
		params?: AssetFileParams,
		options?: { organizationId?: string },
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/assets/${assetId}/files/count`,
				{
					params: this.toAssetFileQueryParams(params),
					headers: organizationIdHeader(
						options?.organizationId ?? params?.OrganizationId,
					),
				},
			);
			return z.number().parse(response);
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to get file count for asset ${assetId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * POST /assets/{assetId}/files — multipart field `File`.
	 */
	async uploadAssetFile(
		assetId: string,
		file: File | Blob,
		options?: {
			organizationId?: string;
			description?: string;
			entityAttachmentRequirementTagId?: string | null;
			onUploadProgress?: (percent: number) => void;
		},
	): Promise<void> {
		try {
			const formData = new FormData();
			const filename =
				file instanceof File && file.name.length > 0 ? file.name : "upload.bin";
			formData.append("File", file, filename);
			appendAttachmentRequirementTagId(
				formData,
				options?.entityAttachmentRequirementTagId,
			);

			await apiService.post(`/assets/${assetId}/files`, formData, {
				params: options?.description
					? { Description: options.description }
					: undefined,
				headers: {
					"Content-Type": "multipart/form-data",
					...organizationIdHeader(options?.organizationId),
				},
				onUploadProgress: (event) => {
					if (!options?.onUploadProgress || !event.total) return;
					options.onUploadProgress(
						Math.round((event.loaded / event.total) * 100),
					);
				},
			});
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to upload file for asset ${assetId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * DELETE /assets/{assetId}/files/{fileId}
	 */
	async deleteAssetFile(
		assetId: string,
		fileId: string,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await apiService.delete(`/assets/${assetId}/files/${fileId}`, {
				headers: organizationIdHeader(options?.organizationId),
			});
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to delete file ${fileId} for asset ${assetId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * PUT /assets/{assetId}/files/{assetFileId}
	 */
	async editAssetFile(
		assetId: string,
		assetFileId: string,
		info: EditAssetFileInfo,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await apiService.put(`/assets/${assetId}/files/${assetFileId}`, info, {
				headers: organizationIdHeader(options?.organizationId),
			});
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to edit file ${assetFileId} for asset ${assetId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * GET /assets/{assetId}/files/{fileId}
	 */
	async getAssetFile(
		assetId: string,
		fileId: string,
		options?: { organizationId?: string },
	): Promise<Blob> {
		try {
			return await apiService.get<Blob>(`/assets/${assetId}/files/${fileId}`, {
				responseType: "blob",
				headers: organizationIdHeader(options?.organizationId),
			});
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to get file ${fileId} for asset ${assetId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * GET /assets/{assetId}/files/{fileId}/thumbnail
	 */
	async getAssetFileThumbnail(
		assetId: string,
		fileId: string,
		options?: { organizationId?: string },
	): Promise<Blob> {
		try {
			return await apiService.get<Blob>(
				`/assets/${assetId}/files/${fileId}/thumbnail`,
				{
					responseType: "blob",
					headers: organizationIdHeader(options?.organizationId),
				},
			);
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to get thumbnail for file ${fileId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * HEAD /assets/{assetId}/files/{fileId}
	 */
	async headAssetFile(
		assetId: string,
		fileId: string,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await apiService.head(`/assets/${assetId}/files/${fileId}`, {
				headers: organizationIdHeader(options?.organizationId),
			});
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to head file ${fileId} for asset ${assetId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * PATCH /assets/{assetId}/files/{fileId} — multipart field `file`.
	 */
	async replaceAssetFile(
		assetId: string,
		fileId: string,
		file: File | Blob,
		options?: {
			organizationId?: string;
			onUploadProgress?: (percent: number) => void;
		},
	): Promise<void> {
		try {
			const formData = new FormData();
			const filename =
				file instanceof File && file.name.length > 0 ? file.name : "upload.bin";
			formData.append("file", file, filename);

			await apiService.patch(`/assets/${assetId}/files/${fileId}`, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
					...organizationIdHeader(options?.organizationId),
				},
				onUploadProgress: (event) => {
					if (!options?.onUploadProgress || !event.total) return;
					options.onUploadProgress(
						Math.round((event.loaded / event.total) * 100),
					);
				},
			});
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to replace file ${fileId} for asset ${assetId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== ASSET NOTES ====================

	/**
	 * Flattens {@link AssetNoteParams} for ASP.NET query-string binding.
	 */
	private toAssetNoteQueryParams(
		params?: AssetNoteParams,
	):
		| Record<string, string | number | boolean | string[] | undefined>
		| undefined {
		return this.toAssetFileQueryParams(params);
	}

	/**
	 * List asset notes with pagination.
	 * GET /assets/{assetId}/notes/{start}/{count}
	 */
	async getAssetNotes(
		assetId: string,
		start: number = 1,
		count: number = 50,
		params?: AssetNoteParams,
		options?: { organizationId?: string },
	): Promise<NoteDisplay[]> {
		try {
			const response = await apiService.get<unknown>(
				`/assets/${assetId}/notes/${start}/${count}`,
				{
					params: this.toAssetNoteQueryParams(params),
					headers: organizationIdHeader(
						options?.organizationId ?? params?.OrganizationId,
					),
				},
			);
			return AssetNoteDisplayArraySchema.parse(response);
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to get notes for asset ${assetId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Count asset notes.
	 * GET /assets/{assetId}/notes/count
	 */
	async getAssetNotesCount(
		assetId: string,
		params?: AssetNoteParams,
		options?: { organizationId?: string },
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/assets/${assetId}/notes/count`,
				{
					params: this.toAssetNoteQueryParams(params),
					headers: organizationIdHeader(
						options?.organizationId ?? params?.OrganizationId,
					),
				},
			);
			return z.number().parse(response);
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to get note count for asset ${assetId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Create an asset note.
	 * POST /assets/{assetId}/notes
	 */
	async createAssetNote(
		assetId: string,
		info: CreateNoteInfo,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			const body = CreateNoteInfoSchema.parse(info);
			await apiService.post(`/assets/${assetId}/notes`, body, {
				headers: organizationIdHeader(options?.organizationId),
			});
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to create note for asset ${assetId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Update an asset note.
	 * PUT /assets/{assetId}/notes/{noteId}
	 */
	async updateAssetNote(
		assetId: string,
		noteId: string,
		info: CreateNoteInfo,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			const body = CreateNoteInfoSchema.parse(info);
			await apiService.put(`/assets/${assetId}/notes/${noteId}`, body, {
				headers: organizationIdHeader(options?.organizationId),
			});
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to update note ${noteId} for asset ${assetId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Delete an asset note.
	 * DELETE /assets/{assetId}/notes/{noteId}
	 */
	async deleteAssetNote(
		assetId: string,
		noteId: string,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await apiService.delete(`/assets/${assetId}/notes/${noteId}`, {
				headers: organizationIdHeader(options?.organizationId),
			});
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to delete note ${noteId} for asset ${assetId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Link an existing record to an asset.
	 * POST /assets/{assetId}/records/{recordId}
	 */
	async linkAssetRecord(
		assetId: string,
		recordId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const response = await apiService.post<ServiceResult>(
				`/assets/${assetId}/records/${recordId}`,
				undefined,
				{
					headers: organizationIdHeader(options?.organizationId),
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw AssetApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to link record ${recordId} to asset ${assetId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Unlink a record from an asset.
	 * DELETE /assets/{assetId}/records/{recordId}
	 */
	async unlinkAssetRecord(
		assetId: string,
		recordId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const response = await apiService.delete<ServiceResult>(
				`/assets/${assetId}/records/${recordId}`,
				{
					headers: organizationIdHeader(options?.organizationId),
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw AssetApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof AssetApiError) {
				throw error;
			}
			throw new AssetApiError(
				`Failed to unlink record ${recordId} from asset ${assetId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	async getAssetTypeAttachmentRequirement(
		assetTypeId: string,
		options?: { organizationId?: string },
	) {
		try {
			return await getTypeAttachmentRequirement(
				"assets",
				assetTypeId,
				options?.organizationId,
			);
		} catch (error) {
			if (error instanceof AssetApiError) throw error;
			throw new AssetApiError(
				`Failed to get attachment requirement for asset type ${assetTypeId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	async putAssetTypeAttachmentRequirement(
		assetTypeId: string,
		minimumTotalAttachments: number,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await putTypeAttachmentRequirement(
				"assets",
				assetTypeId,
				minimumTotalAttachments,
				options?.organizationId,
			);
		} catch (error) {
			if (error instanceof AssetApiError) throw error;
			throw new AssetApiError(
				`Failed to save attachment requirement for asset type ${assetTypeId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	async createAssetTypeAttachmentRequirementTag(
		assetTypeId: string,
		info: {
			name?: string | null;
			description?: string | null;
			allowedKind?: string | null;
			minimumCount: number;
			sortOrder?: number | null;
		},
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await createTypeAttachmentRequirementTag(
				"assets",
				assetTypeId,
				info,
				options?.organizationId,
			);
		} catch (error) {
			if (error instanceof AssetApiError) throw error;
			throw new AssetApiError(
				`Failed to create attachment requirement tag for asset type ${assetTypeId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	async editAssetTypeAttachmentRequirementTag(
		assetTypeId: string,
		tagId: string,
		info: {
			name?: string | null;
			description?: string | null;
			allowedKind?: string | null;
			minimumCount: number;
			sortOrder: number;
		},
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await editTypeAttachmentRequirementTag(
				"assets",
				assetTypeId,
				tagId,
				info,
				options?.organizationId,
			);
		} catch (error) {
			if (error instanceof AssetApiError) throw error;
			throw new AssetApiError(
				`Failed to edit attachment requirement tag ${tagId} for asset type ${assetTypeId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	async deleteAssetTypeAttachmentRequirementTag(
		assetTypeId: string,
		tagId: string,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await deleteTypeAttachmentRequirementTag(
				"assets",
				assetTypeId,
				tagId,
				options?.organizationId,
			);
		} catch (error) {
			if (error instanceof AssetApiError) throw error;
			throw new AssetApiError(
				`Failed to delete attachment requirement tag ${tagId} for asset type ${assetTypeId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}
}

export const assetService = new AssetService();
